import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare, Plus } from 'lucide-react';
import '@speles7172/message-console/styles.css';
import { fetchRecordThreads, type RecordThread } from '../../api/notifications';
import { ConversationView } from '../messaging/ConversationView';
import { NewConversationModal } from '../messaging/NewConversationModal';
import { conversationLabel } from '../messaging/RecordConversations';

interface PortalConversationsProps {
  /** The record the conversations are about, in `THREAD_ENTITY_TYPES`' spelling (`Event`). */
  entityType: string;
  entityId: string;
  /** Its name, for the heading. */
  recordName?: string;
}

const PAGE = 20;

/**
 * An event's conversations, for a client or a vendor in their portal
 * (COMMUNICATIONS-DESIGN D11).
 *
 * The same conversations the office's workspace tab opens — keyed on the
 * event, so one conversation is one conversation whoever reads it — but listed
 * the way somebody who is not the office needs them:
 *
 * ⚠️ **The ones they are on, never "the event's first".** The office's button
 * (`RecordConversations`) opens the record's first conversation or starts one;
 * a portal user is on some of an event's conversations and not others, so that
 * button either opened nothing of theirs or started another beside the one
 * they are on. This lists `GET /threads/for-record/list`, which the server
 * already narrows to the caller's own.
 *
 * ⚠️ **A new one names who it is to.** Started through `NewConversationModal`,
 * which, for anybody below the office, asks for at least one person — the
 * office's button starts one with nobody, and a portal user's first message
 * then reached nobody.
 *
 * Whether it shows at all is the page's (`canMessage`), and what they may do
 * in it the server's.
 */
export function PortalConversations({
  entityType,
  entityId,
  recordName,
}: PortalConversationsProps) {
  const [threads, setThreads] = useState<RecordThread[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  /**
   * The record the list on screen belongs to.
   *
   * ⚠️ **An answer for any other record is dropped.** The vendor dashboard keeps
   * this mounted while the vendor picks another event, so a page still on its
   * way for the last one would otherwise land in — or append to — the new
   * one's list, and "Load more" would walk from the wrong offset. The pages
   * also key it by record, so a draft started about one event is never sent
   * about the next.
   */
  const current = useRef(`${entityType}:${entityId}`);
  current.current = `${entityType}:${entityId}`;

  const load = useCallback(
    (offset: number) => {
      const asked = `${entityType}:${entityId}`;
      return fetchRecordThreads(entityType, entityId, { limit: PAGE, offset })
        .then((page) => {
          if (current.current !== asked) return;
          setThreads((shown) => {
            if (offset === 0) return page.items;
            const seen = new Set(shown.map((thread) => thread.id));
            return [...shown, ...page.items.filter((thread) => !seen.has(thread.id))];
          });
          setNextOffset(page.hasMore ? page.offset + page.limit : null);
          setFailed(false);
        })
        .catch(() => {
          if (current.current === asked && offset === 0) setFailed(true);
        });
    },
    [entityType, entityId],
  );

  useEffect(() => {
    setLoading(true);
    setOpenId(null);
    void load(0).finally(() => setLoading(false));
  }, [load]);

  return (
    <section
      className="bg-white rounded-lg border p-4 space-y-3"
      data-testid="portal-conversations"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <MessageSquare size={18} className="text-primary-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {recordName ? `Conversations about ${recordName}` : 'Conversations'}
            </h3>
            <p className="text-xs text-gray-500">
              Write to the office, and read what they have sent you.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setNewOpen(true)}
          className="flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 flex-shrink-0"
          data-testid="portal-new-conversation"
        >
          <Plus size={14} />
          New conversation
        </button>
      </div>

      {loading ? (
        <div className="h-10 rounded-lg bg-gray-100 animate-pulse" />
      ) : failed ? (
        <p className="text-sm text-red-600">
          Your conversations could not be loaded.{' '}
          <button type="button" className="underline" onClick={() => void load(0)}>
            Try again
          </button>
        </p>
      ) : threads.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="portal-conversations-empty">
          No conversations yet. Start one to write to the office.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {threads.map((thread) => (
            <li key={thread.id}>
              <button
                type="button"
                onClick={() => setOpenId((current) => (current === thread.id ? null : thread.id))}
                aria-expanded={openId === thread.id}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                  openId === thread.id ? 'bg-primary-50 text-primary-700' : 'text-gray-800'
                }`}
                data-testid={`portal-conversation-${thread.id}`}
              >
                <span className="font-medium">{conversationLabel(thread)}</span>
                {thread.status === 'resolved' && (
                  <span className="ml-2 text-xs text-gray-500">Resolved</span>
                )}
                {thread.status === 'closed' && (
                  <span className="ml-2 text-xs text-gray-500">Closed</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {nextOffset !== null && (
        <button
          type="button"
          onClick={() => void load(nextOffset)}
          className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
          data-testid="portal-conversations-more"
        >
          Load more
        </button>
      )}

      {openId && (
        <div className="peles-controls" data-testid="portal-conversation-open">
          <ConversationView threadId={openId} record={{ entityType, entityId }} />
        </div>
      )}

      <NewConversationModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        scope={{ entityType, entityId }}
        onCreated={(threadId) => {
          setNewOpen(false);
          setOpenId(threadId);
          void load(0);
        }}
      />
    </section>
  );
}
